import { useCallback, useRef, useState } from 'react';
import {
  SpaceApiError,
  spaceErrorMessage,
  spacesApi,
  type SpaceOnConflict,
  type SpaceUploadRequestFile,
  type SpaceUploadResult,
  type SpaceUploadTicket,
} from './api';
import { contentTypeFor, formatBytes } from './spaces';

/**
 * The most files one drop or pick may hold. Each file is a ticket, a PUT and a confirmation from this
 * tab, and a drop of 160,000 took the tab down; past this many, an archive uploaded as one file is the
 * way in — the spaces worker unpacks it into a folder beside it (`apps/spaces-worker/src/import`).
 */
export const MAX_FILES_PER_UPLOAD = 2000;

/** A file to upload, with its path relative to the folder it was dropped on (`notes.md`, `q3/notes.md`). */
export interface DroppedFile {
  file: File;
  path: string;
}

// ─── Reading a drop ─────────────────────────────────────────────────────────

/**
 * Operating-system litter a folder drop sweeps up. Skipped only while walking a dropped directory —
 * a person who drags `.DS_Store` in on its own presumably meant to.
 */
const FOLDER_LITTER = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

function readBatch(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

function entryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function walk(entry: FileSystemEntry, prefix: string, out: DroppedFile[], unreadable: string[]): Promise<void> {
  // Past the limit the drop is refused whatever else is in it, so there is no reason to keep reading.
  if (out.length > MAX_FILES_PER_UPLOAD) return;
  if (entry.isFile) {
    try {
      out.push({ file: await entryFile(entry as FileSystemFileEntry), path: `${prefix}${entry.name}` });
    } catch {
      unreadable.push(`${prefix}${entry.name}`);
    }
    return;
  }
  if (!entry.isDirectory) return;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const dir = `${prefix}${entry.name}/`;
  // `readEntries` hands a directory back in batches (100 at a time in Chrome) and signals the end with
  // an empty one, so a single call silently drops everything past the first hundred files.
  for (;;) {
    let batch: FileSystemEntry[];
    try {
      batch = await readBatch(reader);
    } catch {
      unreadable.push(dir);
      return;
    }
    if (batch.length === 0 || out.length > MAX_FILES_PER_UPLOAD) return;
    for (const child of batch) {
      if (child.isFile && FOLDER_LITTER.has(child.name)) continue;
      await walk(child, dir, out, unreadable);
    }
  }
}

/**
 * Every file in a drop, folders walked into relative paths.
 *
 * Must be called from inside the `drop` handler, not after an `await`: `dataTransfer.items` is
 * emptied once the event returns, so the entries are taken synchronously — before this function's
 * first `await` — and only the walking is asynchronous.
 */
export function readDrop(dataTransfer: DataTransfer): Promise<{ files: DroppedFile[]; unreadable: string[] }> {
  const entries: FileSystemEntry[] = [];
  const loose: File[] = [];
  for (const item of Array.from(dataTransfer.items ?? [])) {
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry) entries.push(entry);
    else {
      const file = item.getAsFile();
      if (file) loose.push(file);
    }
  }
  // A browser with no `items` (or no entries API) still fills `files` — flat, but better than nothing.
  if (entries.length === 0 && loose.length === 0) loose.push(...Array.from(dataTransfer.files ?? []));

  return (async () => {
    const files: DroppedFile[] = loose.map((file) => ({ file, path: file.name }));
    const unreadable: string[] = [];
    for (const entry of entries) await walk(entry, '', files, unreadable);
    return { files, unreadable };
  })();
}

/** Files from an `<input type="file">`, keeping the folder structure when it was a directory pick. */
export function filesFromInput(list: FileList | null): DroppedFile[] {
  return Array.from(list ?? [])
    .filter((file) => !(file.webkitRelativePath && FOLDER_LITTER.has(file.name)))
    .map((file) => ({ file, path: file.webkitRelativePath || file.name }));
}

// ─── Hashing and the PUT ────────────────────────────────────────────────────

/**
 * Above this the file is uploaded without a checksum. Hashing reads the whole file into memory, which
 * is fine at 64 MiB and not at 5 GiB; past it, the server's size check after the PUT is what stands
 * between a truncated upload and a stored file.
 */
export const HASH_LIMIT_BYTES = 64 * 1024 * 1024;

async function sha256Hex(file: File): Promise<string | undefined> {
  // `crypto.subtle` exists only in a secure context — https, or localhost. Elsewhere upload unhashed.
  if (file.size > HASH_LIMIT_BYTES || !globalThis.crypto?.subtle) return undefined;
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // A file that cannot be read here cannot be uploaded either; the PUT will say so.
    return undefined;
  }
}

class UploadCancelled extends Error {
  constructor() {
    super('Cancelled');
  }
}

/** What storage said when it refused, from the S3-style XML error body if there is one. */
function storageRefusal(xhr: XMLHttpRequest): string {
  const said = /<Message>([^<]+)<\/Message>/i.exec(xhr.responseText ?? '')?.[1]?.trim();
  return `Storage refused the upload (${xhr.status}${said ? `: ${said}` : ''})`;
}

/**
 * The bytes, straight to storage on the presigned URL.
 *
 * XHR rather than fetch for one reason: upload progress, which fetch still cannot report. Every header
 * the ticket names is sent as given — they are signed, so a header that differs by a byte fails the
 * signature — except `content-length`, which a page may not set and the browser sets itself from the
 * body. No cookie goes with it: storage is another origin and the URL is the credential.
 */
function putToStorage(
  ticket: SpaceUploadTicket,
  file: File,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadCancelled());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.url);
    for (const [name, value] of Object.entries(ticket.headers)) {
      if (name.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(storageRefusal(xhr))));
    // A CORS refusal and a dropped connection are indistinguishable from here — the browser hides both.
    xhr.onerror = () => reject(new Error('The upload to storage failed: the connection dropped, or storage refused this site'));
    xhr.onabort = () => reject(new UploadCancelled());
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

// ─── The tray's state ───────────────────────────────────────────────────────

export type UploadState =
  | 'queued'
  | 'hashing'
  | 'requesting'
  | 'uploading'
  | 'confirming'
  | 'done'
  | 'failed'
  | 'conflict'
  | 'cancelled';

export const UPLOAD_IN_FLIGHT: ReadonlySet<UploadState> = new Set(['queued', 'hashing', 'requesting', 'uploading', 'confirming']);

export interface UploadItem {
  key: string;
  file: File;
  path: string;
  folderId: string;
  /** Where it is going, as the person sees it — `~/research/` — so a tray spanning folders reads plainly. */
  destination: string;
  state: UploadState;
  loaded: number;
  error: string | null;
  /** The name it was stored under when that differs — "Keep both" renames. */
  savedAs: string | null;
}

/** At most this many files per `POST /spaces/uploads` — the server's own limit. */
const REQUEST_BATCH = 200;
/** And at most this many bytes, so the tickets in one request are used before their URLs lapse. */
const GROUP_BYTES = 512 * 1024 * 1024;
/** Concurrent PUTs per batch: enough to fill a connection with small files without starving a large one. */
const PUT_CONCURRENCY = 3;

let keySeq = 0;

/** Splits a batch into ticket requests: by count, and by size, but never a file on its own into nothing. */
function groupsOf(batch: UploadItem[]): UploadItem[][] {
  const groups: UploadItem[][] = [];
  let current: UploadItem[] = [];
  let bytes = 0;
  for (const item of batch) {
    if (current.length > 0 && (current.length >= REQUEST_BATCH || bytes + item.file.size > GROUP_BYTES)) {
      groups.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += item.file.size;
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

export interface SpaceUploads {
  items: UploadItem[];
  /** Whether anything is still moving. */
  busy: boolean;
  start: (input: {
    folderId: string;
    destination: string;
    files: DroppedFile[];
    maxFileBytes: number;
    /** A space's top level holds folders only, so a loose file dropped there is refused before asking. */
    atSpaceRoot: boolean;
  }) => void;
  /** Re-requests one conflicted file, replacing what is in the way or storing beside it under a new name. */
  resolveConflict: (key: string, mode: Exclude<SpaceOnConflict, 'fail'>) => void;
  resolveAllConflicts: (mode: Exclude<SpaceOnConflict, 'fail'>) => void;
  cancel: (key: string) => void;
  dismiss: (key: string) => void;
  clearFinished: () => void;
}

/**
 * Uploads into spaces: ticket, PUT, confirm, per file, with what each is doing held for the tray.
 *
 * `onStored` fires as each file is confirmed (with the folder it went into) rather than once at the
 * end, so a listing can show files arriving during a long batch; the caller debounces.
 */
export function useSpaceUploads(onStored: (folderId: string) => void): SpaceUploads {
  const [items, setItems] = useState<UploadItem[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const controllers = useRef(new Map<string, AbortController>());
  const onStoredRef = useRef(onStored);
  onStoredRef.current = onStored;

  const patch = useCallback((key: string, update: Partial<UploadItem>) => {
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...update } : item)));
  }, []);

  /**
   * Ticket, PUT and confirm for files that all go to one folder, a group at a time.
   *
   * Grouped rather than ticketing the whole drop up front because a ticket's URL expires: ask for two
   * hundred at once over a slow connection and the last ones lapse before their turn comes. A group is
   * at most `REQUEST_BATCH` files and `GROUP_BYTES` bytes, so tickets are issued shortly before they
   * are used, and hashing (which reads each file whole) happens just ahead of the upload it serves.
   */
  const run = useCallback(
    async (batch: UploadItem[], folderId: string, onConflict: SpaceOnConflict) => {
      const createFolders = batch.some((item) => item.path.includes('/'));
      // The controllers this run was started with. A conflict resolved while the rest of its batch is
      // still uploading gets a new one, which this run's cleanup must leave alone.
      const owned = new Map(batch.map((item) => [item.key, controllers.current.get(item.key)]));
      const aborted = (item: UploadItem) => controllers.current.get(item.key)?.signal.aborted ?? false;

      const uploadOne = async (item: UploadItem, ticket: SpaceUploadTicket) => {
        const controller = controllers.current.get(item.key) ?? new AbortController();
        if (controller.signal.aborted) {
          patch(item.key, { state: 'cancelled', error: null });
          return;
        }
        patch(item.key, { state: 'uploading', loaded: 0 });
        try {
          await putToStorage(ticket, item.file, (loaded) => patch(item.key, { loaded }), controller.signal);
          patch(item.key, { state: 'confirming', loaded: item.file.size });
          await spacesApi.completeUpload(ticket.file.id, ticket.versionId);
          patch(item.key, { state: 'done' });
          onStoredRef.current(folderId);
        } catch (err) {
          if (err instanceof UploadCancelled) patch(item.key, { state: 'cancelled', error: null });
          else patch(item.key, { state: 'failed', error: spaceErrorMessage(err, 'The upload failed') });
        }
      };

      for (const group of groupsOf(batch)) {
        const hashes = new Map<string, string | undefined>();
        for (const item of group) {
          if (aborted(item)) continue;
          if (item.file.size <= HASH_LIMIT_BYTES) patch(item.key, { state: 'hashing' });
          hashes.set(item.key, await sha256Hex(item.file));
        }
        // Checked again after hashing: a file cancelled meanwhile must not be ticketed, or it would sit on
        // the server as a pending upload until the sweeper expires it.
        const live = group.filter((item) => hashes.has(item.key) && !aborted(item));
        if (live.length === 0) continue;
        const files: SpaceUploadRequestFile[] = live.map((item) => {
          const sha256 = hashes.get(item.key);
          return { path: item.path, size: item.file.size, contentType: contentTypeFor(item.file), ...(sha256 ? { sha256 } : {}) };
        });
        live.forEach((item) => patch(item.key, { state: 'requesting' }));

        let results: SpaceUploadResult[];
        try {
          ({ uploads: results } = await spacesApi.requestUploads({ folderId, files, onConflict, createFolders }));
        } catch (err) {
          const message = spaceErrorMessage(err, 'The upload could not be started');
          live.forEach((item) => patch(item.key, { state: 'failed', error: message }));
          continue;
        }

        const tasks: Array<{ item: UploadItem; ticket: SpaceUploadTicket }> = [];
        live.forEach((item, index) => {
          // One answer per file, in request order. By position rather than by path, because the server
          // normalizes names (NFC) and a macOS file name arrives decomposed, so the two strings can differ
          // while naming the same file; by path only if the counts ever disagree.
          const result = results.length === live.length ? results[index] : results.find((r) => r.path === item.path);
          if (!result) {
            patch(item.key, { state: 'failed', error: 'The server did not answer for this file' });
          } else if (result.ok) {
            tasks.push({ item, ticket: result.ticket });
            const dropped = (item.path.split('/').pop() ?? '').normalize('NFC').trim();
            patch(item.key, {
              state: 'queued',
              error: null,
              savedAs: result.ticket.file.name.normalize('NFC') !== dropped ? result.ticket.file.name : null,
            });
          } else {
            // Only a plain conflict is offered Replace / Keep both; one that survives `replace` is something else.
            const conflict = result.error.status === 409 && onConflict === 'fail';
            patch(item.key, { state: conflict ? 'conflict' : 'failed', error: result.error.message });
          }
        });

        let next = 0;
        const worker = async () => {
          while (next < tasks.length) {
            const { item, ticket } = tasks[next++]!;
            await uploadOne(item, ticket);
          }
        };
        await Promise.all(Array.from({ length: Math.min(PUT_CONCURRENCY, tasks.length) }, worker));
      }

      batch.forEach((item) => {
        if (controllers.current.get(item.key) === owned.get(item.key)) controllers.current.delete(item.key);
      });
    },
    [patch],
  );

  const start = useCallback<SpaceUploads['start']>(
    ({ folderId, destination, files, maxFileBytes, atSpaceRoot }) => {
      const created: UploadItem[] = files.map(({ file, path }) => {
        const refusal =
          atSpaceRoot && !path.includes('/')
            ? 'Files go inside a folder, not at the top of your space. Open a folder first, or drop a whole folder here.'
            : file.size > maxFileBytes
              ? `Larger than the ${formatBytes(maxFileBytes)} limit for one file`
              : null;
        keySeq += 1;
        return {
          key: `u${keySeq}`,
          file,
          path,
          folderId,
          destination,
          state: refusal ? 'failed' : 'queued',
          loaded: 0,
          error: refusal,
          savedAs: null,
        };
      });
      setItems((prev) => [...prev, ...created]);
      const runnable = created.filter((item) => item.state === 'queued');
      runnable.forEach((item) => controllers.current.set(item.key, new AbortController()));
      if (runnable.length > 0) void run(runnable, folderId, 'fail');
    },
    [run],
  );

  const retry = useCallback(
    (targets: UploadItem[], mode: Exclude<SpaceOnConflict, 'fail'>) => {
      const byFolder = new Map<string, UploadItem[]>();
      for (const item of targets) {
        controllers.current.set(item.key, new AbortController());
        patch(item.key, { state: 'queued', error: null, loaded: 0 });
        byFolder.set(item.folderId, [...(byFolder.get(item.folderId) ?? []), item]);
      }
      byFolder.forEach((group, folderId) => void run(group, folderId, mode));
    },
    [patch, run],
  );

  const resolveConflict = useCallback<SpaceUploads['resolveConflict']>(
    (key, mode) => {
      const item = itemsRef.current.find((i) => i.key === key && i.state === 'conflict');
      if (item) retry([item], mode);
    },
    [retry],
  );

  const resolveAllConflicts = useCallback<SpaceUploads['resolveAllConflicts']>(
    (mode) => retry(itemsRef.current.filter((i) => i.state === 'conflict'), mode),
    [retry],
  );

  const cancel = useCallback(
    (key: string) => {
      const controller = controllers.current.get(key);
      if (controller) controller.abort();
      // Not yet at the PUT: nothing will notice the abort, so mark it here. (A PUT notices on its own.) A
      // file whose ticket was already issued stays pending on the server until its URL lapses and the
      // sweeper expires it — nothing is charged for it meanwhile.
      const item = itemsRef.current.find((i) => i.key === key);
      if (item && (item.state === 'queued' || item.state === 'hashing' || item.state === 'requesting')) patch(key, { state: 'cancelled' });
    },
    [patch],
  );

  const dismiss = useCallback((key: string) => setItems((prev) => prev.filter((i) => i.key !== key)), []);

  const clearFinished = useCallback(
    () => setItems((prev) => prev.filter((i) => UPLOAD_IN_FLIGHT.has(i.state) || i.state === 'conflict')),
    [],
  );

  return {
    items,
    busy: items.some((i) => UPLOAD_IN_FLIGHT.has(i.state)),
    start,
    resolveConflict,
    resolveAllConflicts,
    cancel,
    dismiss,
    clearFinished,
  };
}

/** Whether an error from a spaces call was a version conflict — someone saved in between. */
export function isConflict(err: unknown): boolean {
  return err instanceof SpaceApiError && err.status === 409;
}
